import React, {useEffect, useState} from 'react';
import {ColumnType, ExtraHeadingColumn} from '../types';
import { isFirstMobileColumn, isLastMobileColumn, isMobileColumn, } from '../Table';
import ExpandableIcon from "@sharedComponents/ExpandableIcon";
import './Scss/table-row.scss';

interface ITableRowProps {
    rowId?: string
    columns: ColumnType[]
    columnsMobile: string[]
    rowData: any
    className?: string
    renderForServer?: boolean
    breakpointMobile?: string
    extraHeadingRow?: ExtraHeadingColumn[];
}

const TableRow = (props: ITableRowProps) => {
    const [expanded, setExpanded] = useState(false);
    const expandedClass = expanded ? 'expanded' : '';
    const isExpandable =
        !props.renderForServer && (props.columns && props.columns.length > 2);

    const breakpointMobileClass = props.breakpointMobile ?? 'md';
    const [extraHeadingRowColumns, setExtraHeadingRowColumns] = useState<ExtraHeadingColumn[]>([]);

    useEffect(() => {
        if (props.extraHeadingRow && props.extraHeadingRow.length > 0) {
            const normalizeExtraHeadingRow = (columns: ColumnType[], extraHeadingRow: ExtraHeadingColumn[]) => {
                const normalizedRow: any = [];
                let headingRowIndex = 0;
                let remainingColspan = extraHeadingRow[headingRowIndex]?.colspan ?? 0;
                let isFirstInGroup = true;

                columns.forEach(() => {
                    if (remainingColspan === 0) {
                        headingRowIndex++;
                        remainingColspan = extraHeadingRow[headingRowIndex]?.colspan ?? 0;
                        isFirstInGroup = true; // Reset for the new group
                    }

                    normalizedRow.push({
                        ...extraHeadingRow[headingRowIndex],
                        text: isFirstInGroup ? extraHeadingRow[headingRowIndex]?.text : '', // Show text only on the first column
                    });

                    remainingColspan--;
                    isFirstInGroup = false; // After the first column, set this to false
                });

                return normalizedRow;
            };

            const normalizedExtraHeadingRow = normalizeExtraHeadingRow(props.columns, props.extraHeadingRow);
            setExtraHeadingRowColumns(normalizedExtraHeadingRow);
        }
    }, [props.columns, props.extraHeadingRow]);
    

    const onRowClick = () => {
        if (isExpandable) {
            setExpanded(!expanded);
        }
    }

    const renderMainColumns = () => {
        let addedMobileExpandIcon = false;
        return props.columns.map((column: ColumnType) => {
            const isMobileCol = isMobileColumn(column.key, props.columnsMobile);
            const isFirstMobileCol = isFirstMobileColumn(
                column.key,
                props.columnsMobile
            );
            const isLastMobileCol = isLastMobileColumn(
                column.key,
                props.columnsMobile
            );
            const firstMobileColClass = isFirstMobileCol ? 'first-column' : '';
            const lastMobileColClass = isLastMobileCol ? 'last-column' : '';
            const visibleOnMobileClass = isMobileCol ? 'visible-on-mobile' : '';
            const expandableClass = isExpandable ? 'expandable' : '';
            const cellData = props.rowData[column.key];
            const hasHiddenData = props.columns.length !== props.columnsMobile.length;
            const expandIcon =
                visibleOnMobileClass && !addedMobileExpandIcon && hasHiddenData ? (
                    <ExpandableIcon type="primary" isExpanded={expanded} />
                ) : (
                    ''
                )
            if (expandIcon) {
                addedMobileExpandIcon = true;
            }
            const renderKey = `${props.rowId}_col_${column.key}`;
            const columnContent = column.format
                ? column.format(cellData, props.rowData)
                : cellData;
            return (
                <td
                    key={renderKey}
                    className={`table-column ${column.key} ${visibleOnMobileClass} ${firstMobileColClass} ${lastMobileColClass} ${expandableClass} ${breakpointMobileClass}`}>
                    {isExpandable && expandIcon}
                    {columnContent}
                </td>
            );
        })
    }

    return (
        <>
            <tr
                onClick={onRowClick}
                className={`table-row table-row--avoidpage-break ${props.className} ${expandedClass} ${breakpointMobileClass}`}>
                {renderMainColumns()}
            </tr>
            {!props.renderForServer && (
                <tr
                    className={`mobile-table-row mobile-expand-row ${props.className} ${expandedClass} ${breakpointMobileClass}`}>
                    <td colSpan={props.columnsMobile?.length || 2}>
                        <div
                            style={{'--num-table-row-content-cols': props.columns && props.columns.length - 2} as React.CSSProperties }
                            className={`content ${expandedClass}`}>
                            {props.columns.map((column: ColumnType, index: number) => {
                                if (!isMobileColumn(column.key, props.columnsMobile)) {
                                    const cellData = props.rowData[column.key];
                                    const renderKey = `${props.rowId}_col_${column.key}_mobile`;
                                    // Check if heading text needs to be rendered
                                    const headingText =
                                        props.extraHeadingRow &&
                                        props.extraHeadingRow.length > 0 &&
                                        extraHeadingRowColumns &&
                                        extraHeadingRowColumns[index]?.text;
                                    
                                    return (
                                        <React.Fragment key={renderKey}>
                                            {headingText && <h5 className={'group-heading'}>{headingText}</h5>}
                                            <div>
                                                {column.text && <label>{column.text}:</label>}
                                                {column.format
                                                    ? column.format(cellData, props.rowData)
                                                    : cellData}
                                            </div>
                                        </React.Fragment>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

export default TableRow;
